import React, { useState, useEffect, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
  Annotation
} from "react-simple-maps";
import locationPin from '../images/kk-location-map-pins.png';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Cache for geocoded results
const geocodeCache = new Map();

// Helper function to get the state code from an address
const getStateFromAddress = (address) => {
  if (!address || typeof address !== 'string') return null;
  
  const stateRegex = /\b([A-Z]{2})\b/;
  const match = address.match(stateRegex);
  return match && match.length > 0 ? match[1] : null;
};

// Function to get the center of a state geography
const getStateCenter = (geo) => {
  if (!geo || !geo.id) return null;
  
  // First check if we have a predefined center
  if (stateCenters[geo.id]) {
    return stateCenters[geo.id];
  }
  
  // Fallback - try to use centroid from the geography object
  try {
    if (geo.properties && geo.properties.centroid) {
      return [geo.properties.centroid[0], geo.properties.centroid[1]];
    }
    
    if (geo.centroid) {
      return [geo.centroid[0], geo.centroid[1]];
    }
  } catch (error) {
    console.error("Error getting state center:", error);
  }
  
  // Return null if no center could be determined
  return null;
};

// State code to FIPS code mapping
const stateCodeToFips = {
  "AL": "01", "AK": "02", "AZ": "04", "AR": "05", "CA": "06", "CO": "08", "CT": "09",
  "DE": "10", "DC": "11", "FL": "12", "GA": "13", "HI": "15", "ID": "16", "IL": "17",
  "IN": "18", "IA": "19", "KS": "20", "KY": "21", "LA": "22", "ME": "23", "MD": "24",
  "MA": "25", "MI": "26", "MN": "27", "MS": "28", "MO": "29", "MT": "30", "NE": "31",
  "NV": "32", "NH": "33", "NJ": "34", "NM": "35", "NY": "36", "NC": "37", "ND": "38",
  "OH": "39", "OK": "40", "OR": "41", "PA": "42", "RI": "44", "SC": "45", "SD": "46",
  "TN": "47", "TX": "48", "UT": "49", "VT": "50", "VA": "51", "WA": "53", "WV": "54",
  "WI": "55", "WY": "56"
};

// Predefined center coordinates for each state
const stateCenters = {
  "01": [-86.8073, 32.7990], // Alabama
  "02": [-149.4937, 64.2008], // Alaska
  "04": [-111.6602, 34.2744], // Arizona
  "05": [-92.4426, 34.8938], // Arkansas
  "06": [-119.4179, 37.1841], // California
  "08": [-105.5478, 38.9972], // Colorado
  "09": [-72.7622, 41.6219], // Connecticut
  "10": [-75.5277, 38.9108], // Delaware
  "11": [-77.0147, 38.8951], // District of Columbia
  "12": [-81.6900, 27.8333], // Florida
  "13": [-83.4429, 32.6415], // Georgia
  "15": [-155.5828, 19.8968], // Hawaii
  "16": [-114.6130, 44.3509], // Idaho
  "17": [-89.3776, 40.0417], // Illinois
  "18": [-86.2816, 39.8942], // Indiana
  "19": [-93.0977, 41.9383], // Iowa
  "20": [-98.3804, 38.4937], // Kansas
  "21": [-85.3021, 37.5347], // Kentucky
  "22": [-91.9623, 31.0689], // Louisiana
  "23": [-69.2428, 45.3695], // Maine
  "24": [-76.7902, 39.0550], // Maryland
  "25": [-71.7957, 42.2373], // Massachusetts
  "26": [-84.6056, 43.4343], // Michigan
  "27": [-94.3053, 45.7296], // Minnesota
  "28": [-89.7309, 32.7673], // Mississippi
  "29": [-92.4580, 38.3566], // Missouri
  "30": [-109.6333, 46.9048], // Montana
  "31": [-99.7951, 41.4925], // Nebraska
  "32": [-116.6312, 39.3289], // Nevada
  "33": [-71.5811, 43.6805], // New Hampshire
  "34": [-74.5210, 40.1907], // New Jersey
  "35": [-106.1126, 34.4071], // New Mexico
  "36": [-75.5268, 42.9538], // New York
  "37": [-79.3877, 35.5557], // North Carolina
  "38": [-100.4659, 47.4501], // North Dakota
  "39": [-82.7755, 40.2862], // Ohio
  "40": [-97.5170, 35.5889], // Oklahoma
  "41": [-120.5583, 44.1419], // Oregon
  "42": [-77.7996, 40.8781], // Pennsylvania
  "44": [-71.5562, 41.6762], // Rhode Island
  "45": [-80.9066, 33.9169], // South Carolina
  "46": [-100.2263, 44.4443], // South Dakota
  "47": [-86.3505, 35.8580], // Tennessee
  "48": [-99.3312, 31.4757], // Texas
  "49": [-111.6703, 39.4225], // Utah
  "50": [-72.6658, 44.0687], // Vermont
  "51": [-78.8164, 37.5215], // Virginia
  "53": [-120.5911, 47.3826], // Washington
  "54": [-80.8986, 38.6409], // West Virginia
  "55": [-89.9941, 44.6243], // Wisconsin
  "56": [-107.5512, 42.9957]  // Wyoming
};

// State name mapping
const stateMapping = {
  "01": "Alabama",
  "02": "Alaska",
  "04": "Arizona",
  "05": "Arkansas",
  "06": "California",
  "08": "Colorado",
  "09": "Connecticut",
  "10": "Delaware",
  "11": "District of Columbia",
  "12": "Florida",
  "13": "Georgia",
  "15": "Hawaii",
  "16": "Idaho",
  "17": "Illinois",
  "18": "Indiana",
  "19": "Iowa",
  "20": "Kansas",
  "21": "Kentucky",
  "22": "Louisiana",
  "23": "Maine",
  "24": "Maryland",
  "25": "Massachusetts",
  "26": "Michigan",
  "27": "Minnesota",
  "28": "Mississippi",
  "29": "Missouri",
  "30": "Montana",
  "31": "Nebraska",
  "32": "Nevada",
  "33": "New Hampshire",
  "34": "New Jersey",
  "35": "New Mexico",
  "36": "New York",
  "37": "North Carolina",
  "38": "North Dakota",
  "39": "Ohio",
  "40": "Oklahoma",
  "41": "Oregon",
  "42": "Pennsylvania",
  "44": "Rhode Island",
  "45": "South Carolina",
  "46": "South Dakota",
  "47": "Tennessee",
  "48": "Texas",
  "49": "Utah",
  "50": "Vermont",
  "51": "Virginia",
  "53": "Washington",
  "54": "West Virginia",
  "55": "Wisconsin",
  "56": "Wyoming"
};

// State population data (2020 Census estimates)
const statePopulations = {
  "01": "5,024,279", // Alabama
  "02": "733,391", // Alaska
  "04": "7,151,502", // Arizona
  "05": "3,011,524", // Arkansas
  "06": "39,538,223", // California
  "08": "5,773,714", // Colorado
  "09": "3,605,944", // Connecticut
  "10": "989,948", // Delaware
  "11": "689,545", // District of Columbia
  "12": "21,538,187", // Florida
  "13": "10,711,908", // Georgia
  "15": "1,455,271", // Hawaii
  "16": "1,839,106", // Idaho
  "17": "12,812,508", // Illinois
  "18": "6,785,528", // Indiana
  "19": "3,190,369", // Iowa
  "20": "2,937,880", // Kansas
  "21": "4,505,836", // Kentucky
  "22": "4,657,757", // Louisiana
  "23": "1,362,359", // Maine
  "24": "6,177,224", // Maryland
  "25": "7,029,917", // Massachusetts
  "26": "10,077,331", // Michigan
  "27": "5,706,494", // Minnesota
  "28": "2,961,279", // Mississippi
  "29": "6,154,913", // Missouri
  "30": "1,084,225", // Montana
  "31": "1,961,504", // Nebraska
  "32": "3,104,614", // Nevada
  "33": "1,377,529", // New Hampshire
  "34": "9,288,994", // New Jersey
  "35": "2,117,522", // New Mexico
  "36": "20,201,249", // New York
  "37": "10,439,388", // North Carolina
  "38": "779,094", // North Dakota
  "39": "11,799,448", // Ohio
  "40": "3,959,353", // Oklahoma
  "41": "4,237,256", // Oregon
  "42": "13,002,700", // Pennsylvania
  "44": "1,097,379", // Rhode Island
  "45": "5,118,425", // South Carolina
  "46": "886,667", // South Dakota
  "47": "6,910,840", // Tennessee
  "48": "29,145,505", // Texas
  "49": "3,271,616", // Utah
  "50": "643,077", // Vermont
  "51": "8,631,393", // Virginia
  "53": "7,705,281", // Washington
  "54": "1,793,716", // West Virginia
  "55": "5,893,718", // Wisconsin
  "56": "576,851"  // Wyoming
};

// FIPS code to state abbreviation mapping
const stateAbbreviations = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
  "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL",
  "18": "IN", "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD",
  "25": "MA", "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE",
  "32": "NV", "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV",
  "55": "WI", "56": "WY"
};

// City fallback coordinates for when geocoding fails
const cityFallbacks = {
  "Phoenix, AZ": [-112.0740, 33.4484],
  "Las Vegas, NV": [-115.1398, 36.1699], 
  "Dallas, TX": [-96.7970, 32.7767],
  "Boston, MA": [-71.0589, 42.3601],
  "Los Angeles, CA": [-118.2437, 34.0522],
  "Chicago, IL": [-87.6298, 41.8781],
  "New York, NY": [-74.0060, 40.7128],
  "Miami, FL": [-80.1918, 25.7617],
  "Seattle, WA": [-122.3321, 47.6062],
  "Denver, CO": [-104.9903, 39.7392],
  "Atlanta, GA": [-84.3880, 33.7490],
  "Austin, TX": [-97.7431, 30.2672],
  "Portland, OR": [-122.6765, 45.5231],
  "San Diego, CA": [-117.1611, 32.7157],
  "San Francisco, CA": [-122.4194, 37.7749],
  "Nashville, TN": [-86.7816, 36.1627],
  "Jacksonville, FL": [-81.6557, 30.3322],
  "Charlotte, NC": [-80.8431, 35.2271],
  "Indianapolis, IN": [-86.1581, 39.7684],
  "Columbus, OH": [-82.9988, 39.9612],
  "Cleveland, OH": [-81.6944, 41.4993],
  "Pittsburgh, PA": [-79.9959, 40.4406],
  "Milwaukee, WI": [-87.9065, 43.0389],
  "Albuquerque, NM": [-106.6504, 35.0844],
  "Tucson, AZ": [-110.9747, 32.2226],
  "Sacramento, CA": [-121.4944, 38.5816],
  "Coronado, CA": [-117.1831, 32.6859],
  "Central City, CO": [-105.5142, 39.8014],
  "Demorest, GA": [-83.5457, 34.5698],
  "Salt Lake City, UT": [-111.8910, 40.7608],
  "Raleigh, NC": [-78.6382, 35.7796],
  "Durham, NC": [-78.8986, 35.9940],
  "Richmond, VA": [-77.4360, 37.5407],
  "Asheville, NC": [-82.5515, 35.5951],
  "Madison, WI": [-89.4012, 43.0731],
  "Kansas City, MO": [-94.5786, 39.0997],
  "Minneapolis, MN": [-93.2650, 44.9778],
  "Cincinnati, OH": [-84.5120, 39.1031],
  "Milwaukee, WI": [-87.9065, 43.0389],
  "St. Louis, MO": [-90.1994, 38.6270],
  "Charleston, SC": [-79.9311, 32.7765]
};

// Helper function to extract city and state from address
const extractCityState = (address) => {
  if (!address || typeof address !== 'string') return null;
  
  // Try to extract city, state from the last line of address
  const lines = address.split('\n');
  const lastLine = lines[lines.length - 1];
  
  // Look for pattern: City, ST or City, ST zipcode
  const cityStateMatch = lastLine.match(/([^,]+),\s*([A-Z]{2})(?:\s+\d{5})?/);
  if (cityStateMatch) {
    return `${cityStateMatch[1].trim()}, ${cityStateMatch[2]}`;
  }
  
  return null;
};

const geocodeAddress = async (address) => {
  if (geocodeCache.has(address)) {
    return geocodeCache.get(address);
  }

  try {
    // Add a delay between requests to comply with usage policy
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Format address and ensure US-specific search
    const searchQuery = `${address}, USA`;
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=us`,
      {
        headers: {
          'Accept': 'application/json',
          // Required by Nominatim's usage policy
          'User-Agent': 'KrossKast-Location-Service'
        }
      }
    );

    const data = await response.json();
    
    if (data && data.length > 0) {
      const result = {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon)
      };
      
      geocodeCache.set(address, result);
      return result;
    }

    // If initial search fails, try with simplified address
    const simplifiedAddress = address
      .replace(/#\w+/g, '') // Remove unit numbers
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
    
    const retryResponse = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(simplifiedAddress)}, USA&countrycodes=us`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'KrossKast-Location-Service'
        }
      }
    );

    const retryData = await retryResponse.json();
    
    if (retryData && retryData.length > 0) {
      const result = {
        latitude: parseFloat(retryData[0].lat),
        longitude: parseFloat(retryData[0].lon)
      };
      
      geocodeCache.set(address, result);
      return result;
    }

    // Try fallback coordinates based on city/state
    const cityState = extractCityState(address);
    if (cityState && cityFallbacks[cityState]) {
      console.log(`Using fallback coordinates for: ${address} -> ${cityState}`);
      const fallbackCoords = cityFallbacks[cityState];
      const result = {
        latitude: fallbackCoords[1],
        longitude: fallbackCoords[0]
      };
      geocodeCache.set(address, result);
      return result;
    }

    console.warn(`Geocoding failed for address: ${address}`);
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    
    // Try fallback coordinates even on error
    const cityState = extractCityState(address);
    if (cityState && cityFallbacks[cityState]) {
      console.log(`Using fallback coordinates after error for: ${address} -> ${cityState}`);
      const fallbackCoords = cityFallbacks[cityState];
      const result = {
        latitude: fallbackCoords[1],
        longitude: fallbackCoords[0]
      };
      geocodeCache.set(address, result);
      return result;
    }
    
    return null;
  }
};

// Major US cities with coordinates
const majorCities = [
  // Largest US cities
  { name: "New York", coordinates: [-74.0060, 40.7128], population: 8804190, state: "36" },
  { name: "Los Angeles", coordinates: [-118.2437, 34.0522], population: 3898747, state: "06" },
  { name: "Chicago", coordinates: [-87.6298, 41.8781], population: 2746388, state: "17" },
  { name: "Houston", coordinates: [-95.3698, 29.7604], population: 2304580, state: "48" },
  { name: "Phoenix", coordinates: [-112.0740, 33.4484], population: 1608139, state: "04" },
  { name: "Philadelphia", coordinates: [-75.1652, 39.9526], population: 1603797, state: "42" },
  { name: "San Antonio", coordinates: [-98.4936, 29.4241], population: 1434625, state: "48" },
  { name: "Dallas", coordinates: [-96.7970, 32.7767], population: 1304379, state: "48" },
  { name: "San Francisco", coordinates: [-122.4194, 37.7749], population: 873965, state: "06" },
  { name: "Atlanta", coordinates: [-84.3880, 33.7490], population: 498715, state: "13" },
  { name: "Miami", coordinates: [-80.1918, 25.7617], population: 442241, state: "12" },
  
  // Alabama
  { name: "Birmingham", coordinates: [-86.8025, 33.5186], population: 200733, state: "01" },
  { name: "Mobile", coordinates: [-88.0399, 30.6954], population: 187041, state: "01" },
  
  // Alaska
  { name: "Anchorage", coordinates: [-149.9003, 61.2181], population: 291247, state: "02" },
  
  // Arizona
  { name: "Tucson", coordinates: [-110.9747, 32.2226], population: 542629, state: "04" },
  
  // Arkansas
  { name: "Little Rock", coordinates: [-92.2896, 34.7465], population: 197312, state: "05" },
  { name: "Fayetteville", coordinates: [-94.1574, 36.0626], population: 85257, state: "05" },
  
  // California
  { name: "Sacramento", coordinates: [-121.4944, 38.5816], population: 500930, state: "06" },
  { name: "San Diego", coordinates: [-117.1611, 32.7157], population: 1386932, state: "06" },
  
  // Colorado
  { name: "Denver", coordinates: [-104.9903, 39.7392], population: 715522, state: "08" },
  { name: "Colorado Springs", coordinates: [-104.8214, 38.8339], population: 464871, state: "08" },
  
  // Connecticut
  { name: "Hartford", coordinates: [-72.6823, 41.7658], population: 121054, state: "09" },
  
  // Delaware
  { name: "Dover", coordinates: [-75.5268, 39.1582], population: 37538, state: "10" },
  
  // Florida
  { name: "Orlando", coordinates: [-81.3789, 28.5383], population: 307573, state: "12" },
  { name: "Tampa", coordinates: [-82.4572, 27.9506], population: 399700, state: "12" },
  
  // Georgia
  { name: "Savannah", coordinates: [-81.0912, 32.0809], population: 147780, state: "13" },
  
  // Hawaii
  { name: "Honolulu", coordinates: [-157.8583, 21.3069], population: 350964, state: "15" },
  
  // Idaho
  { name: "Boise", coordinates: [-116.2023, 43.6150], population: 228959, state: "16" },
  { name: "Idaho Falls", coordinates: [-112.0340, 43.4927], population: 62888, state: "16" },
  
  // Illinois
  { name: "Springfield", coordinates: [-89.6501, 39.7817], population: 114230, state: "17" },
  
  // Indiana
  { name: "Indianapolis", coordinates: [-86.1581, 39.7684], population: 876384, state: "18" },
  
  // Iowa
  { name: "Des Moines", coordinates: [-93.6091, 41.5868], population: 214133, state: "19" },
  { name: "Cedar Rapids", coordinates: [-91.6656, 41.9779], population: 132301, state: "19" },
  
  // Kansas
  { name: "Wichita", coordinates: [-97.3375, 37.6872], population: 397532, state: "20" },
  { name: "Kansas City", coordinates: [-94.6274, 39.1141], population: 156607, state: "20" },
  
  // Kentucky
  { name: "Louisville", coordinates: [-85.7585, 38.2527], population: 633045, state: "21" },
  { name: "Lexington", coordinates: [-84.5037, 38.0406], population: 322570, state: "21" },
  
  // Louisiana
  { name: "New Orleans", coordinates: [-90.0715, 29.9511], population: 383997, state: "22" },
  { name: "Baton Rouge", coordinates: [-91.1403, 30.4583], population: 227470, state: "22" },
  
  // Maine
  { name: "Portland, ME", coordinates: [-70.2553, 43.6591], population: 68408, state: "23" },
  
  // Maryland
  { name: "Baltimore", coordinates: [-76.6122, 39.2904], population: 576498, state: "24" },
  
  // Massachusetts
  { name: "Boston", coordinates: [-71.0589, 42.3601], population: 675647, state: "25" },
  
  // Michigan
  { name: "Detroit", coordinates: [-83.0458, 42.3314], population: 639111, state: "26" },
  
  // Minnesota
  { name: "Minneapolis", coordinates: [-93.2650, 44.9778], population: 429954, state: "27" },
  { name: "Duluth", coordinates: [-92.1005, 46.7867], population: 86697, state: "27" },
  
  // Mississippi
  { name: "Jackson", coordinates: [-90.1848, 32.2988], population: 153701, state: "28" },
  { name: "Biloxi", coordinates: [-88.8853, 30.3960], population: 45908, state: "28" },
  
  // Missouri
  { name: "St. Louis", coordinates: [-90.1994, 38.6270], population: 301578, state: "29" },
  
  // Montana
  { name: "Billings", coordinates: [-108.5007, 45.7833], population: 117116, state: "30" },
  { name: "Missoula", coordinates: [-114.0190, 46.8721], population: 76784, state: "30" },
  
  // Nebraska
  { name: "Omaha", coordinates: [-95.9345, 41.2565], population: 468262, state: "31" },
  
  // Nevada
  { name: "Las Vegas", coordinates: [-115.1398, 36.1699], population: 641903, state: "32" },
  { name: "Reno", coordinates: [-119.8138, 39.5296], population: 255601, state: "32" },
  
  // New Hampshire
  { name: "Concord", coordinates: [-71.5376, 43.2081], population: 43976, state: "33" },
  
  // New Jersey
  { name: "Newark, NJ", coordinates: [-74.1724, 40.7357], population: 311549, state: "34" },
  
  // New Mexico
  { name: "Albuquerque", coordinates: [-106.6504, 35.0844], population: 562599, state: "35" },
  { name: "Santa Fe", coordinates: [-105.9378, 35.6870], population: 84683, state: "35" },
  
  // New York
  { name: "Albany", coordinates: [-73.7562, 42.6526], population: 97856, state: "36" },
  { name: "Buffalo", coordinates: [-78.8784, 42.8864], population: 278349, state: "36" },
  
  // North Carolina
  { name: "Charlotte", coordinates: [-80.8431, 35.2271], population: 874579, state: "37" },
  { name: "Raleigh", coordinates: [-78.6382, 35.7796], population: 467665, state: "37" },
  
  // North Dakota
  { name: "Fargo", coordinates: [-96.7898, 46.8772], population: 125990, state: "38" },
  { name: "Bismarck", coordinates: [-100.7837, 46.8083], population: 73622, state: "38" },
  
  // Ohio
  { name: "Columbus", coordinates: [-82.9988, 39.9612], population: 905748, state: "39" },
  { name: "Cleveland", coordinates: [-81.6944, 41.4993], population: 372624, state: "39" },
  
  // Oklahoma
  { name: "Oklahoma City", coordinates: [-97.5164, 35.4676], population: 681054, state: "40" },
  { name: "Tulsa", coordinates: [-95.9928, 36.1540], population: 403090, state: "40" },
  
  // Oregon
  { name: "Portland", coordinates: [-122.6765, 45.5231], population: 652503, state: "41" },
  { name: "Eugene", coordinates: [-123.0868, 44.0521], population: 176654, state: "41" },
  
  // Pennsylvania
  { name: "Pittsburgh", coordinates: [-79.9959, 40.4406], population: 302971, state: "42" },
  
  // Rhode Island
  { name: "Providence", coordinates: [-71.4128, 41.8240], population: 179883, state: "44" },
  
  // South Carolina
  { name: "Charleston", coordinates: [-79.9311, 32.7765], population: 150227, state: "45" },
  { name: "Columbia", coordinates: [-81.0348, 34.0007], population: 136632, state: "45" },
  
  // South Dakota
  { name: "Sioux Falls", coordinates: [-96.7003, 43.5446], population: 192517, state: "46" },
  { name: "Rapid City", coordinates: [-103.2310, 44.0805], population: 74703, state: "46" },
  
  // Tennessee
  { name: "Nashville", coordinates: [-86.7816, 36.1627], population: 689447, state: "47" },
  { name: "Memphis", coordinates: [-90.0490, 35.1495], population: 633104, state: "47" },
  
  // Texas
  { name: "Austin", coordinates: [-97.7431, 30.2672], population: 961855, state: "48" },
  { name: "El Paso", coordinates: [-106.4985, 31.7619], population: 678815, state: "48" },
  
  // Utah
  { name: "Salt Lake City", coordinates: [-111.8910, 40.7608], population: 199723, state: "49" },
  { name: "Provo", coordinates: [-111.6585, 40.2339], population: 116618, state: "49" },
  
  // Vermont
  { name: "Burlington", coordinates: [-73.2121, 44.4759], population: 42417, state: "50" },
  
  // Virginia
  { name: "Richmond", coordinates: [-77.4360, 37.5407], population: 226610, state: "51" },
  { name: "Virginia Beach", coordinates: [-76.0581, 36.8529], population: 459470, state: "51" },
  
  // Washington
  { name: "Seattle", coordinates: [-122.3321, 47.6062], population: 737015, state: "53" },
  { name: "Spokane", coordinates: [-117.4260, 47.6588], population: 222081, state: "53" },
  
  // West Virginia
  { name: "Charleston, WV", coordinates: [-81.6326, 38.3498], population: 48018, state: "54" },
  
  // Wisconsin
  { name: "Milwaukee", coordinates: [-87.9065, 43.0389], population: 577222, state: "55" },
  
  // Wyoming
  { name: "Cheyenne", coordinates: [-104.8202, 41.1400], population: 65132, state: "56" },
  { name: "Casper", coordinates: [-106.3131, 42.8666], population: 58446, state: "56" },
  
  // District of Columbia
  { name: "Washington DC", coordinates: [-77.0369, 38.9072], population: 689545, state: "11" }
];

const USMap = ({ locations = [], onPinClick }) => {
  const [geocodedLocations, setGeocodedLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [errors, setErrors] = useState([]);
  const [position, setPosition] = useState({ 
    coordinates: [-98.5795, 39.8283], // Center of continental US
    zoom: 1.8
  });
  const [selectedState, setSelectedState] = useState(null);
  const [stateLocationCounts, setStateLocationCounts] = useState({});
  const [stateLocations, setStateLocations] = useState({});
  const [weatherData, setWeatherData] = useState(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    const geocodeLocations = async () => {
      setIsLoading(true);
      setErrors([]);
      
      try {
        const geocoded = await Promise.all(
          locations.map(async (location) => {
            if (!location.address) {
              setErrors(prev => [...prev, `Missing address for location: ${location.name || 'Unknown'}`]);
              return null;
            }

            const coords = await geocodeAddress(location.address);
            if (!coords) {
              setErrors(prev => [...prev, `Failed to geocode address: ${location.address}`]);
              return null;
            }

            return { ...location, ...coords };
          })
        );

        setGeocodedLocations(geocoded.filter(Boolean));
      } catch (error) {
        console.error('Geocoding process failed:', error);
        setErrors(prev => [...prev, 'Failed to process locations']);
      } finally {
        setIsLoading(false);
      }
    };

    geocodeLocations();
  }, [locations]);

  // Process locations to determine state counts and group locations by state
  useEffect(() => {
    const processLocationStates = async () => {
      if (!geocodedLocations || !geocodedLocations.length) return;
      
      const counts = {};
      const stateLocationMap = {};
      
      geocodedLocations.forEach(location => {
        if (!location || !location.address) return;
        
        const stateCode = getStateFromAddress(location.address);
        if (stateCode) {
          const fipsCode = stateCodeToFips[stateCode];
          if (fipsCode) {
            // Update counts
            counts[fipsCode] = (counts[fipsCode] || 0) + 1;
            
            // Group locations by state
            if (!stateLocationMap[fipsCode]) {
              stateLocationMap[fipsCode] = [];
            }
            stateLocationMap[fipsCode].push(location);
          }
        }
      });
      
      setStateLocationCounts(counts);
      setStateLocations(stateLocationMap);
    };
    
    processLocationStates();
  }, [geocodedLocations]);

  useEffect(() => {
    const mapElement = mapRef.current;
    
    // Prevent default wheel behavior to avoid accidental zooming/scrolling
    const preventScroll = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    if (mapElement) {
      mapElement.addEventListener('wheel', preventScroll, { passive: false });
    }

    // Cleanup
    return () => {
      if (mapElement) {
        mapElement.removeEventListener('wheel', preventScroll);
      }
    };
  }, []);

  const handleMoveEnd = (newPosition) => {
    // Ensure we have valid coordinates before updating state
    if (newPosition && Array.isArray(newPosition.coordinates) && newPosition.coordinates.length === 2) {
      setPosition(newPosition);
    }
  };

  const handleStateClick = (geo) => {
    try {
      // If a state is already selected, reset to full US view
      if (selectedState && selectedState.id === geo.id) {
        setSelectedState(null);
        setWeatherData(null);
        setPosition({
          coordinates: [-98.5795, 39.8283],
          zoom: 1.8
        });
        return;
      }

      // Verify the geo object is valid
      if (!geo || !geo.id) {
        console.warn("Invalid geography object", geo);
        return;
      }

      // Get the state ID and look up predefined center coordinates
      const stateId = geo.id;
      if (stateId && stateCenters[stateId]) {
        // Use predefined center coordinates for the selected state
        const centerCoords = stateCenters[stateId];
        
        console.log(`Zooming to state ${stateId} (${stateMapping[stateId]}) at coordinates:`, centerCoords);
        
        // Set selected state and zoom in
        setSelectedState(geo);
        setPosition({
          coordinates: centerCoords,
          zoom: 6  // Higher zoom level for state view
        });
        
        // Fetch weather data for the selected state
        fetchWeatherData(stateId);
      } else {
        console.warn("Could not find center coordinates for state", stateId);
      }
    } catch (error) {
      console.error("Error in handleStateClick:", error);
    }
  };

  // Function to reset to US view
  const resetToUSView = () => {
    setSelectedState(null);
    setPosition({
      coordinates: [-98.5795, 39.8283],
      zoom: 1.8
    });
  };

  // Function to fetch weather data for a state
  const fetchWeatherData = async (stateId) => {
    if (!stateId) return;
    
    setIsLoadingWeather(true);
    setWeatherData(null);
    
    try {
      // Get state name and center coordinates
      const stateName = stateMapping[stateId];
      const coordinates = stateCenters[stateId];
      
      if (!stateName || !coordinates) {
        throw new Error("Invalid state data");
      }
      
      // Use a different API that doesn't require an API key - OpenMeteo
      const lat = coordinates[1]; // latitude is second value
      const lon = coordinates[0]; // longitude is first value
      
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&temperature_unit=fahrenheit&wind_speed_unit=mph`
      );
      
      if (!response.ok) {
        throw new Error(`Weather API error: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Map weather code to condition
      const weatherCodeMapping = {
        0: "Clear sky",
        1: "Mainly clear", 
        2: "Partly cloudy", 
        3: "Overcast",
        45: "Fog", 
        48: "Depositing rime fog",
        51: "Light drizzle", 
        53: "Moderate drizzle", 
        55: "Dense drizzle",
        56: "Light freezing drizzle", 
        57: "Dense freezing drizzle",
        61: "Slight rain", 
        63: "Moderate rain", 
        65: "Heavy rain",
        66: "Light freezing rain", 
        67: "Heavy freezing rain",
        71: "Slight snow fall", 
        73: "Moderate snow fall", 
        75: "Heavy snow fall",
        77: "Snow grains",
        80: "Slight rain showers", 
        81: "Moderate rain showers", 
        82: "Violent rain showers",
        85: "Slight snow showers", 
        86: "Heavy snow showers",
        95: "Thunderstorm", 
        96: "Thunderstorm with slight hail", 
        99: "Thunderstorm with heavy hail"
      };
      
      // Format the weather data
      setWeatherData({
        temp: Math.round(data.current.temperature_2m),
        condition: weatherCodeMapping[data.current.weather_code] || "Unknown",
        description: weatherCodeMapping[data.current.weather_code] || "Weather conditions unknown",
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        location: stateName // Use the state name instead of city name
      });
    } catch (error) {
      console.error("Error fetching weather:", error);
      setWeatherData({ error: "Unable to load weather data" });
    } finally {
      setIsLoadingWeather(false);
    }
  };

  return (
    <div className="us-map-container" ref={mapRef}>
      {selectedState && (
        <div className="state-view-controls">
          <button onClick={resetToUSView} className="back-to-us-btn">
            Back to US View
          </button>
          <h3 className="state-title">
            {(selectedState.id && stateMapping[selectedState.id]) || 
             (selectedState.properties && selectedState.properties.name) || 
             'Selected State'}
          </h3>
          <div className="state-population" style={{
            fontWeight: "bold",
            fontSize: "14px",
            color: "#444",
            marginBottom: "8px"
          }}>
            Population: {selectedState.id && statePopulations[selectedState.id] ? statePopulations[selectedState.id] : 'Data not available'}
          </div>

          {/* Weather information */}
          <div className="state-weather" style={{
            fontSize: "14px",
            color: "#444",
            marginBottom: "12px",
            padding: "5px",
            background: "rgba(255,255,255,0.7)",
            borderRadius: "4px"
          }}>
            {isLoadingWeather ? (
              <div style={{ textAlign: "center" }}>Loading weather data...</div>
            ) : weatherData ? (
              weatherData.error ? (
                <div style={{ color: "#666" }}>{weatherData.error}</div>
              ) : (
                <div>
                  <strong>Current Weather:</strong> {weatherData.temp}°F, {weatherData.condition}
                  <div>Humidity: {weatherData.humidity}%, Wind: {weatherData.windSpeed} mph</div>
                </div>
              )
            ) : (
              <div style={{ color: "#666" }}>Weather data unavailable</div>
            )}
          </div>
          
          <div className="location-count">
            {selectedState.id && stateLocationCounts[selectedState.id] ? (
              <span>
                <strong>{stateLocationCounts[selectedState.id]}</strong> location{stateLocationCounts[selectedState.id] !== 1 ? 's' : ''} in this state
              </span>
            ) : (
              <span>No locations in this state</span>
            )}
      </div>
          
          {selectedState.id && stateLocations[selectedState.id] && stateLocations[selectedState.id].length > 0 && (
            <div className="location-list">
              <div className="location-list-header">Location names:</div>
              <ul>
                {stateLocations[selectedState.id].map((location, index) => (
                  <li key={index} onClick={() => onPinClick && location && onPinClick(location)}>
                    {location && location.name ? location.name : 'Unnamed Location'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "rgba(255, 255, 255, 0.8)",
          padding: "10px",
          borderRadius: "5px",
          zIndex: 1000
        }}>
          Loading locations...
        </div>
      )}

      {errors.length > 0 && (
        <div style={{
          position: "absolute",
          top: 0,
          right: 0,
          background: "rgba(255, 0, 0, 0.1)",
          padding: "10px",
          borderRadius: "5px",
          maxWidth: "300px",
          maxHeight: "150px",
          overflow: "auto",
          zIndex: 1000
        }}>
          {errors.map((error, index) => (
            <div key={index} style={{ color: "red", fontSize: "12px" }}>
              {error}
            </div>
          ))}
        </div>
      )}

      <ComposableMap 
        projection="geoAlbersUsa"
        width={1400}
        height={800}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={selectedState ? 4 : position.zoom}
          maxZoom={selectedState ? 12 : position.zoom}
          translateExtent={[
            [-1000, -1000],
            [2000, 2000]
          ]}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) => (
              <>
                {geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                    fill={selectedState && selectedState.id === geo.id ? "#a9a9a9" : "#D3D3D3"}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: {
                        fill: selectedState && selectedState.id === geo.id ? "#a9a9a9" : "#D3D3D3",
                      outline: "none"
                    },
                    hover: {
                      fill: "#A9A9A9",
                      outline: "none"
                    }
                  }}
                    onClick={() => handleStateClick(geo)}
                  />
                ))}

                {/* State abbreviations - only shown in US view */}
                {!selectedState && geographies.map(geo => {
                  if (!geo || !geo.id) return null;
                  const centroid = getStateCenter(geo);
                  if (!centroid) return null;
                  
                  const abbreviation = stateAbbreviations[geo.id];
                  if (!abbreviation) return null;
                  
                  return (
                    <Marker key={`state-abbr-${geo.id}`} coordinates={centroid}>
                      <text
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fill="#444444"
                        fontSize={position.zoom >= 4 ? 14 : position.zoom >= 3.5 ? 12 : position.zoom >= 2.5 ? 10 : 8}
                        fontWeight="bold"
                        style={{ 
                          pointerEvents: "none",
                          textShadow: "0px 0px 3px rgba(255,255,255,0.8)",
                          opacity: position.zoom < 1.5 ? 0.7 : 1 // Fade out labels at very low zoom
                        }}
                      >
                        {abbreviation}
                      </text>
                    </Marker>
                  );
                })}
              </>
            )}
          </Geographies>

          {geocodedLocations.map((location, index) => (
            <Marker
              key={index}
              coordinates={[location.longitude, location.latitude]}
              onClick={() => onPinClick && onPinClick(location)}
              onMouseEnter={() => setHoveredLocation(location)}
              onMouseLeave={() => setHoveredLocation(null)}
            >
              <g transform="translate(-8, -16)">
                <image
                  href={locationPin}
                  width={16}
                  height={16}
                style={{ cursor: "pointer" }}
              />
              </g>
              {hoveredLocation === location && (
                <foreignObject
                  x={5}
                  y={-16}
                  width={160}
                  height={75}
                  style={{ overflow: 'visible' }}
                >
                  <div style={{
                    background: "white",
                    padding: "3px",
                    borderRadius: "2px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                    fontSize: "10px"
                  }}>
                    <strong>{location.name}</strong>
                    <br />
                    {location.address}
                  </div>
                </foreignObject>
              )}
            </Marker>
          ))}
          
          {/* City labels - only show when a state is selected */}
          {majorCities
            .filter((city, _, arr) => {
              // Basic validation
              if (!city || !city.coordinates || !Array.isArray(city.coordinates) || city.coordinates.length < 2) {
                return false;
              }

              // Show all major cities for all states when a state is selected
              if (selectedState && selectedState.id) {
                if (position.zoom >= 12) {
                  return true; // Show all cities in the list
                } else if (position.zoom >= 10) {
                  return city.population > 20000;
                } else if (position.zoom >= 8) {
                  return city.population > 50000;
                } else if (position.zoom >= 6) {
                  // Show all cities with population > 100,000 OR the two largest cities in each state
                  const stateCities = arr.filter(c => c.state === city.state);
                  const sorted = [...stateCities].sort((a, b) => b.population - a.population);
                  const isTop2 = sorted.slice(0, 2).some(c => c.name === city.name);
                  return city.population > 100000 || isTop2;
                } else {
                  return city.population > 200000;
                }
              }
              // Hide all cities in the US view
              return false;
            })
            .map(({ name, coordinates }) => (
              <Marker
                key={`city-${name}`}
                coordinates={coordinates}
              >
                <g>
                  <circle
                    r={1.5}
                    fill="#ffffff"
                    stroke="#000000"
                    strokeWidth={0.3}
                  />
                  <text
                    textAnchor="middle"
                    y={-4}
                    fontSize={position.zoom >= 12 ? 8 : position.zoom >= 10 ? 7 : position.zoom >= 8 ? 6 : position.zoom >= 6 ? 5 : 4}
                    fontWeight="bold"
                    fill="#ffffff"
                    opacity={0.9}
                    style={{
                      pointerEvents: "none",
                      textShadow: "0px 0px 2px rgba(0,0,0,0.9)"
                    }}
                  >
                    {name}
                  </text>
                </g>
              </Marker>
            ))
          }
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
};

export default USMap; 